"use client";

import React from "react";
import { TbTruckDelivery, TbCalendarEvent } from "react-icons/tb";
import { FiArrowRight } from "react-icons/fi";

import { formatDateToBR } from "../utils/date";

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const numericValue =
    typeof value === "number"
      ? value
      : Number(String(value).replace(/[^0-9.-]/g, ""));

  if (Number.isFinite(numericValue)) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(numericValue);
  }

  return String(value).includes("$") ? String(value) : `$${value}`;
};

const normalizeStatusLabel = (status) => {
  if (!status) return "Pending";

  return String(status)
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getStatusClasses = (status) => {
  const normalized = String(status || "").toLowerCase();

  if (
    normalized.includes("delivered") ||
    normalized.includes("completed") ||
    normalized.includes("paid")
  ) {
    return "bg-emerald-400 text-zinc-950";
  }

  if (
    normalized.includes("cancel") ||
    normalized.includes("rejected") ||
    normalized.includes("failed")
  ) {
    return "bg-rose-500 text-white";
  }

  return "bg-amber-400 text-zinc-950";
};

const isVehicleOperable = (value) => {
  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    value === "true" ||
    value === "OP"
  );
};

const getVehicleLabel = (vehicle) => {
  const parts = [vehicle?.year, vehicle?.make, vehicle?.model].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : "Vehicle information unavailable";
};

export default function LoadList({
  loads = [],
  selectedLoad,
  onSelectLoad,
  activeStatus = "All",
  meta,
  onPrev,
  onNext,
  drivers = [],
}) {
  return (
    <div
      className="w-full lg:w-[28rem] p-1 ml-2 sm:p-2 text-white h-[70vh] lg:h-[90vh] rounded-lg
                 bg-transparent flex flex-col min-h-0"
    >
      <h2 className="text-xl sm:text-2xl font-bold mb-2 text-center mr-3">
        {`${activeStatus} Loads`}
      </h2>

      <div className="my-3 mr-3 text-xs sm:text-sm">
        <div className="flex gap-2 justify-center">
          <button
            className="px-4 py-2 rounded-xl border border-white/10 bg-[#1c1c1f] text-white transition hover:bg-[#242428] disabled:opacity-50"
            disabled={(meta?.current_page ?? 1) <= 1}
            onClick={onPrev}
          >
            Previous
          </button>

          <button
            className="px-4 py-2 rounded-xl border border-white/10 bg-[#1c1c1f] text-white transition hover:bg-[#242428] disabled:opacity-50"
            disabled={(meta?.current_page ?? 1) >= (meta?.last_page ?? 1)}
            onClick={onNext}
          >
            Next
          </button>
        </div>

        <div className="mt-2 text-center text-zinc-300">
          Page <strong className="text-white">{meta?.current_page ?? 1}</strong> of{" "}
          <strong className="text-white">{meta?.last_page ?? 1}</strong> • Total{" "}
          <strong className="text-white">{meta?.total ?? 0}</strong>
        </div>
      </div>

      <hr className="border-t border-white/10 mr-3" />

      <div className="flex-1 overflow-y-auto min-h-0 pt-3 pr-3">
        <ul className="space-y-5 flex flex-col items-center">
          {loads.map((load) => {
            const driverId = String(load.driver_id);
            const driver = Array.isArray(drivers)
              ? drivers.find((d) => String(d.id) === driverId)
              : null;

            const driverName = driver
              ? `${driver.user?.first_name || ""} ${driver.user?.last_name || ""}`.trim()
              : "No Driver Assigned";

            const driverAvatar = driver?.user?.profile_picture_path
              ? `https://new.cajuu.app/storage/${driver.user.profile_picture_path}`
              : "https://new.cajuu.app/storage/profile_pictures/profile_picture.png";

            const items =
              Array.isArray(load.load_items) && load.load_items.length > 0
                ? load.load_items
                : Array.isArray(load.vehicles)
                ? load.vehicles
                : [];

            const statusText = normalizeStatusLabel(
              load.status || load.load_status?.load_status_name || "pending"
            );

            const paymentRate = formatMoney(load.payment_rate);

            const paymentType =
              load.payment_type && load.payment_type !== "Select payment type"
                ? load.payment_type
                : null;

            const paymentMethod =
              load.payment_method && load.payment_method !== "Select payment method"
                ? load.payment_method
                : null;

            const isSelected = selectedLoad?.id === load.id;

            return (
              <li
                key={load.id}
                className={[
                  "w-full rounded-[28px] cursor-pointer transition-all duration-200 overflow-hidden",
                  "border backdrop-blur-sm",
                  "bg-[linear-gradient(135deg,rgba(36,36,40,0.98)_0%,rgba(24,24,27,0.98)_52%,rgba(31,31,35,0.98)_100%)]",
                  isSelected
                    ? "border-amber-400 shadow-[0_0_0_1px_rgba(251,191,36,0.15),0_18px_40px_rgba(0,0,0,0.45)]"
                    : "border-white/10 shadow-[0_14px_30px_rgba(0,0,0,0.35)] hover:border-white/20 hover:-translate-y-[1px]",
                ].join(" ")}
                onClick={() => {
                  onSelectLoad?.(load);
                }}
              >
                <div className="p-5 sm:p-6">
                  <div className="border-b border-white/10 pb-1 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm sm:text-sm font-semibold text-zinc-100 break-words">
                        Order ID: {load.order_id || "No Order ID"}
                      </p>
                    </div>

                    <span
                      className={[
                        "shrink-0 rounded-full px-4 py-1 text-xs sm:text-sm font-bold mb-1 mt-0",
                        getStatusClasses(statusText),
                      ].join(" ")}
                    >
                      {statusText}
                    </span>
                  </div>

                  <div className="mt-2 space-y-3">
                    {Array.isArray(items) && items.length > 0 ? (
                      items.map((vehicle, idx) => {
                        const operable = isVehicleOperable(vehicle?.is_operable);

                        return (
                          <div
                            key={vehicle?.id ?? idx}
                            className="flex items-start gap-3 min-w-0"
                          >
                            <TbTruckDelivery
                              className="mt-1 shrink-0 text-[1.25rem] text-zinc-200"
                              aria-hidden="true"
                            />

                            <div className="min-w-0">
                              <p className="text-lg sm:text-[1.05rem] font-semibold leading-snug text-white break-words">
                                {getVehicleLabel(vehicle)}
                                <span
                                  className={`ml-2 text-sm font-bold ${
                                    operable ? "text-emerald-400" : "text-rose-400"
                                  }`}
                                >
                                  {operable ? "OP" : "INOP"}
                                </span>
                              </p>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="flex items-start gap-3 min-w-0">
                        <TbTruckDelivery
                          className="mt-1 shrink-0 text-[1.25rem] text-zinc-200"
                          aria-hidden="true"
                        />
                        <p className="text-lg sm:text-[1.05rem] font-semibold leading-snug text-white break-words">
                          Vehicle information unavailable
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="mt-2 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                    <div className="min-w-0">
                      <p className="text-lg sm:text-[1.0rem] font-medium text-zinc-100 leading-snug break-words">
                        {load.pickup_city}, {load.pickup_state}
                      </p>
                      <div className="flex items-center gap-2 text-[.8rem] text-zinc-400">
                        <TbCalendarEvent className="shrink-0" aria-hidden="true" />
                        <span>{formatDateToBR(load.expected_pickup_date)}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-center text-zinc-400">
                      <span className="h-4 w-4 rounded-full border-2 border-zinc-300/90" />
                      <FiArrowRight className="mx-2 text-[1.2rem]" />
                      <span className="h-4 w-4 rounded-full border-2 border-zinc-300/90" />
                    </div>

                    <div className="min-w-0 text-right">
                      <p className="text-lg sm:text-[1.0rem] font-medium text-zinc-100 leading-snug break-words">
                        {load.delivery_city}, {load.delivery_state}
                      </p>
                      <div className="flex items-center justify-end gap-2 text-[.8rem] text-zinc-400">
                        <TbCalendarEvent className="shrink-0" aria-hidden="true" />
                        <span>{formatDateToBR(load.expected_dropoff_date)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <img
                      src={driverAvatar}
                      alt={driverName}
                      className="w-5 h-5 rounded-full border border-white/15 object-cover bg-zinc-700"
                      onError={(e) => {
                        e.currentTarget.src = "/img/user-avatar.png";
                      }}
                    />
                    <div className="min-w-0">
                      <p className="text-[.8rem] sm:text-[1.05rem] text-zinc-100 break-words">
                        Driver: {driverName}
                      </p>
                    </div>
                  </div>

                  <div className="mt-2 border-t border-white/10 pt-1 flex items-end justify-between gap-4">
                    <div className="min-w-0">
                      {paymentRate ? (
                        <p className="text-[1.2rem] leading-none font-bold text-emerald-400">
                          {paymentRate}
                        </p>
                      ) : (
                        <p className="text-base text-zinc-500">No payment rate</p>
                      )}
                    </div>

                    <div className="text-right text-base sm:text-[1.05rem] font-medium text-zinc-300 flex flex-wrap items-center justify-end gap-2">
                      {paymentType && <span>{paymentType}</span>}
                      {paymentType && paymentMethod && (
                        <span className="text-zinc-500">·</span>
                      )}
                      {paymentMethod && <span>{paymentMethod}</span>}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}